import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import Axios from "axios";

import { Button, ButtonToolbar, Heading, Table, Text } from "rsuite";
import { SortType } from "rsuite-table";

import constants from "utils/constants";
import { formatDateWithNameOfMonth } from "utils/date-helper";
import { getModalTableHeight } from "utils/measurement-helper";
import { getSorted, Paths } from "utils/sort-halper";

import { Mailbox } from "store/environments/tenants/mailboxes/mailboxes-types";
import { OperationHistory } from "store/common/operation-types";

import { loadMailboxesHistory } from "api/environments/tenants/mailboxes-api";
import { getErrorMessage } from "api/defaults";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import { getTenantId } from "store/environments/tenants/tenants-selectors";

import { setModalLoaderVisibility } from "store/common/modal-window/modal-window-slices";
import { setErrorResponseMessageForModal } from "store/common/initialization/initialization-actions";

import ModalContainer from "components/Common/Modal/ModalContainer";
import PopoverCell from "components/Common/PopoverCell/PopoverCell";

import { HistoryContext } from "../Mailboxes";

import "./History.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;
    open: boolean;

    setOpen?: (open: boolean) => void;
}

export type TableColumn = Paths<OperationHistory<Mailbox>>;

const columns: Record<string, TableColumn> = {
    editor: "editor",
    editionDate: "editionDate",
    creator: "data.creator",
    creationDate: "data.creationDate",
};

const History: React.FC<Props> = ({ open, setOpen }: Props) => {
    const cancelTokenSource = Axios.CancelToken.source();

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const mailbox = useContext(HistoryContext);

    const [historyLogs, setHistoryLogs] = useImmer<
        ReadonlyArray<OperationHistory<Mailbox>>
    >([]);

    const [sortColumn, setSortColumn] = useState<TableColumn>(
        columns.editionDate
    );
    const [sortType, setSortType] = useState<SortType>("desc");

    const title = t("tenantConfig.captureMailboxes.mailboxHistory.title");

    const tableHeight = getModalTableHeight(historyLogs.length);

    const fetchData = () => {
        dispatch(setModalLoaderVisibility(true));
        dispatch(setErrorResponseMessageForModal(""));

        Promise.all([fetchMailboxesHistory()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    dispatch(setErrorResponseMessageForModal(message));
                }
            })
            .finally(() => {
                dispatch(setModalLoaderVisibility(false));
            });
    };

    const fetchMailboxesHistory = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadMailboxesHistory(
                activeEnvironmentId,
                activeTenantId,
                mailbox!.id,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setHistoryLogs(
                    getSorted([...payload], sortColumn as TableColumn, sortType)
                );

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSortColumn = (sortColumn: string, sortType: any) => {
        setSortColumn(sortColumn as TableColumn);
        setSortType(sortType);

        setHistoryLogs((draft) =>
            getSorted(draft, sortColumn as TableColumn, sortType)
        );
    };

    const handleModalClose = () => {
        setSortColumn(columns.editionDate);
        setSortType("desc");

        setOpen?.(false);
    };

    useEffect(() => {
        if (open && activeEnvironmentId) {
            fetchData();
        }
    }, [open, activeEnvironmentId]);

    const header = <Heading level={1}>{title}</Heading>;
    const footer = (
        <ButtonToolbar>
            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                type="submit"
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("deliverables.packages.history.close")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <>
            <ModalContainer
                className={"mailboxes-history__modal"}
                open={open}
                header={header}
                footer={footer}
                onClose={handleModalClose}
            >
                <Table
                    shouldUpdateScroll={false}
                    virtualized={true}
                    data={historyLogs}
                    height={tableHeight}
                    sortColumn={sortColumn}
                    sortType={sortType}
                    onSortColumn={handleSortColumn}
                >
                    <Table.Column flexGrow={2} sortable={true}>
                        <Table.HeaderCell>
                            {t(`tenantConfig.captureMailboxes.table.createdBy`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey={columns.creator}>
                            {(rowData: OperationHistory<Mailbox>) => (
                                <PopoverCell
                                    dataKey={columns.creator}
                                    rowData={rowData}
                                />
                            )}
                        </Table.Cell>
                    </Table.Column>

                    <Table.Column flexGrow={1.5} sortable={true}>
                        <Table.HeaderCell>
                            {t(`tenantConfig.captureMailboxes.table.createdOn`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey={columns.creationDate}>
                            {(rowData: OperationHistory<Mailbox>) => (
                                <Text>
                                    {rowData.data.creationDate
                                        ? formatDateWithNameOfMonth(
                                              new Date(
                                                  rowData.data.creationDate
                                              )
                                          )
                                        : t(`unknown`)}
                                </Text>
                            )}
                        </Table.Cell>
                    </Table.Column>

                    <Table.Column flexGrow={2} sortable={true}>
                        <Table.HeaderCell>
                            {t(`tenantConfig.captureMailboxes.table.editedBy`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey={columns.editor}>
                            {(rowData: OperationHistory<Mailbox>) => (
                                <PopoverCell
                                    dataKey={columns.editor}
                                    rowData={rowData}
                                />
                            )}
                        </Table.Cell>
                    </Table.Column>

                    <Table.Column flexGrow={1.5} sortable={true}>
                        <Table.HeaderCell>
                            {t(`tenantConfig.captureMailboxes.table.editedOn`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey={columns.editionDate}>
                            {(rowData: OperationHistory<Mailbox>) => (
                                <Text>
                                    {rowData.editionDate
                                        ? formatDateWithNameOfMonth(
                                              new Date(rowData.editionDate)
                                          )
                                        : t(`unknown`)}
                                </Text>
                            )}
                        </Table.Cell>
                    </Table.Column>
                </Table>
            </ModalContainer>
        </>
    );
};

export default History;
